import React, { useMemo, useState } from 'react';

import { Caption, Foot, Grid, Note, Panel, Pill, plural, Row, Table } from './parts';
import { clock } from './model';
import { downloadCsv } from './exports';
import { measurementGroups } from '../data/measurements';

/* ── System health ──────────────────────────────────────────── */

const COVERAGE = [
    { label: 'AC PQ meters', value: '10', of: '10', note: '5 locations × 2 independent paths' },
    { label: 'DC power meters', value: '14', of: '14', note: '2 DC mains + 12 inverter feeds' },
    { label: 'Thermal sensing points', value: '15', of: '16', note: 'J06-B data is stale', warn: true },
    { label: 'Insulation monitors', value: '2', of: '2', note: 'Independent IMDs on Bus A and Bus B' },
    { label: 'DC-capable residual sensors', value: '2', of: '2', note: 'AC+DC RMS · DC–1 kHz band' }
];

const ARCHITECTURE = [
    ['1. Field instruments', 'AC power-quality meters at the incomer, rectifier input, bypass, UPS output and PDU, plus isolated DC meters, thermal sensors, IMDs and the BMS. Acquisition stays local to each device.'],
    ['2. Edge gateways', 'Read compatible Modbus RTU/TCP registers and normalize units, device identity, timestamps and quality flags.'],
    ['3. Site data platform', 'Publish through an approved MQTT, OPC UA or HTTPS integration into the historian and facility monitoring system.'],
    ['4. Operator dashboard', 'Show read-only measurements, trends and an alarm workflow, with per-channel freshness and coverage.']
];

const RECORDS = [
    ['Instrument inventory', 'Meter and sensor IDs, locations, ratios, ranges and polarity'],
    ['Alarm configuration', 'Approved thresholds, delay, hysteresis and reset policy'],
    ['Time and calibration', 'Clock source, accuracy, calibration and due dates'],
    ['Coverage and retention', 'Expected points, quality flags, acquisition and storage']
];

export function Health({ sim }) {
    const { now, acks, resetAcks, setView } = sim;
    const acknowledged = Object.keys(acks).length;

    return (
        <>
            <div className="dash-controls dash-controls--spread">
                <span className="dash-axis">Simulated device inventory · No live connection</span>
                <button type="button" className="dash-link" onClick={resetAcks} disabled={acknowledged === 0}>Reset acknowledgments →</button>
            </div>

            <ul className="dash-coverage">
                {COVERAGE.map(item => (
                    <li key={item.label}>
                        <span>{item.label}</span>
                        <strong>{item.value}<small>/ {item.of}</small></strong>
                        <p>{item.note}</p>
                        {item.warn && <Pill tone="warn">1 stale</Pill>}
                    </li>
                ))}
            </ul>

            <Grid>
                <Panel title="Telemetry gateways" sub="Edge acquisition and transport" badge={<Pill tone="info">2/2 simulated</Pill>}>
                    <Table>
                        <thead>
                            <tr>
                                <th scope="col">Gateway</th>
                                <th scope="col">Bus</th>
                                <th scope="col" className="is-num">Latency</th>
                                <th scope="col" className="is-num">Clock offset</th>
                                <th scope="col">State</th>
                            </tr>
                        </thead>
                        <tbody>
                            {['A', 'B'].map(bus => (
                                <tr key={bus}>
                                    <th scope="row">
                                        EDGE-{bus}
                                        <span className="dash-table__note">Last display update {clock(now)}</span>
                                    </th>
                                    <td>{bus}</td>
                                    <td className="is-num">26 ms</td>
                                    <td className="is-num">2 ms</td>
                                    <td><Pill tone="info">Simulated</Pill></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    <Row label="Dashboard refresh" note="Display cadence; not waveform acquisition">2 s</Row>
                    <Row label="Historian series" note="Power trend samples">5-minute intervals</Row>
                    <Row label="Fast electrical events" note="Need a local recorder; examples here are synthesized"><Pill tone="info">Demo captures</Pill></Row>
                    <Row label="AC PQ capture coverage" note="Harmonics 2–50; synthetic waveforms and flicker registers"><Pill tone="info">10 simulated meters</Pill></Row>
                    <Row label="Network connection" note="No device endpoints or credentials configured"><Pill>Disconnected</Pill></Row>
                    <Foot>A gateway reporting online does not validate every channel behind it. Every reading carries its own source timestamp, quality flag and acquisition basis.</Foot>
                </Panel>

                <Panel title="Data quality exceptions" sub="Keep unavailable data visible" badge={<Pill tone="warn">1 stale channel</Pill>}>
                    <div className="dash-exception">
                        <h3>J06-B · temperature sample stale</h3>
                        <p>The most recent valid reading is kept only as historical context, and left out of the current-temperature maximum and healthy-status conclusions.</p>
                        <p className="dash-conditions__meta">
                            <Pill>Stale</Pill>
                            <span>Sensor location: Bus B, joint 06</span>
                        </p>
                        <button type="button" className="dash-link" onClick={() => setView('thermal')}>View thermal coverage →</button>
                    </div>

                    <Row label="Validity states" note="Applied per channel">Good · Stale · Missing · Invalid</Row>
                    <Row label="Derived readings" note="Input age and quality carry through to the result">Input-dependent</Row>
                    <Row label="Calibration due" note="Shared date for this inventory">2027-04-01</Row>
                    <Row label="Measurement uncertainty" note="Populated from the complete instrument chain"><Pill>To be specified</Pill></Row>
                    <Row label="AC PQ method evidence" note="IEC class, edition, validation and CT/VT chain"><Pill>Not commissioned</Pill></Row>
                    <Foot>A last-known value must never silently appear as a current healthy measurement. Missing instruments stay labeled “Not instrumented”, rather than showing a false zero.</Foot>
                </Panel>
            </Grid>

            <Panel title="Integration architecture" sub="Data flow · no connectors are active in this demo" badge={<Pill>Disconnected</Pill>}>
                <ol className="dash-arch">
                    {ARCHITECTURE.map(([title, text]) => (
                        <li key={title}>
                            <strong>{title}</strong>
                            <p>{text}</p>
                        </li>
                    ))}
                </ol>
                <Foot>Protocols are options to validate against the selected equipment. The browser does not connect directly to Modbus devices, execute switching commands or replace protective hardware.</Foot>
            </Panel>

            <Grid>
                <Panel title="Commissioning records" sub="What a connected site needs">
                    {RECORDS.map(([label, note]) => (
                        <Row label={label} note={note} key={label}><Pill tone="info">Per project</Pill></Row>
                    ))}
                    <div className="dash-panel__after">
                        <button type="button" className="dash-link" onClick={() => setView('library')}>Open the measurement library →</button>
                    </div>
                </Panel>

                <Panel title="Session" sub="Local interaction state only">
                    <p className="dash-prose">Alarm acknowledgments are kept in this browser tab. Resetting restores them to unacknowledged. It does not clear the conditions themselves or affect any equipment.</p>
                    <div className="dash-panel__after">
                        <button type="button" className="dash-btn" onClick={resetAcks} disabled={acknowledged === 0}>
                            Reset acknowledgments
                        </button>
                        <span className="dash-axis">{plural(acknowledged, 'acknowledgment')} recorded</span>
                    </div>
                </Panel>
            </Grid>
        </>
    );
}

/* ── Measurement library ────────────────────────────────────── */

const REFERENCES = [
    { title: 'NVIDIA · 800 VDC data-center architecture', url: 'https://www.nvidia.com/en-eu/data-center/technologies/800-vdc-architecture/', text: 'Architecture context for the illustrative 800 V pole-to-pole system; not a site-specific electrical design.' },
    { title: 'Bender · ISOMETER iso685', url: 'https://www.bender.de/en/products/insulation-monitoring/isometer-iso685/', text: 'Insulation monitoring, system capacitance, connection monitoring and self-test for unearthed AC/DC systems.' },
    { title: 'Bender · Residual-current measurement principles', url: 'https://www.bender.de/en/know-how/technology/tn-s/tt-system/how-does-residual-current-monitoring-work/', text: 'Explains DC-sensitive residual monitoring. Grounded-system RCM guidance does not replace an IMD on this floating IT example.' },
    { title: 'Yokogawa · WT5000 power measurement', url: 'https://tmi.yokogawa.com/solutions/products/power-analyzers/wt5000/', text: 'Reference for synchronized voltage, current and power acquisition, DC accuracy and waveform bandwidth; no specific installed instrument is implied.' },
    { title: 'IEC 61000-4-30:2025 · PQ measurement methods', url: 'https://webstore.iec.ch/en/publication/71611', text: 'The current IEC measurement-method framework. Method classes and documented instrument evidence are separate from operating acceptance limits.' },
    { title: 'IEEE 519-2022 · Harmonic control at the PCC', url: 'https://standards.ieee.org/ieee/519/10677/', text: 'Steady-state harmonic assessment applies at the identified point of common coupling. A spot THD reading does not establish compliance.' },
    { title: 'HIOKI · AC measurement equations', url: 'https://manual.hioki.com/en/PW4001/PW4001/specifications/basic_measurement_items_equations.html', text: 'Primary definitions for RMS, power, fundamental components, reactive power and unbalance. Meter calculation conventions must be retained.' },
    { title: 'Schneider Electric · THD and TDD', url: 'https://product-help.schneider-electric.com/PowerLogic-PM8000/en-us/content/09-pq/thd-and-tdd.htm', text: 'Distinguishes harmonic distortion denominators and the maximum-demand current used for TDD.' },
    { title: 'HIOKI · Flicker measurement settings', url: 'https://manual.hioki.com/en/PW4001/PW4001/displaying_numerically/iec_flicker_measurement_settings.html', text: 'Instrument settings for Pst observation intervals and the number of Pst intervals used for Plt.' }
];

const ALL = 'all';

export function Library({ sim }) {
    const [query, setQuery] = useState('');
    const [group, setGroup] = useState(ALL);

    const total = useMemo(() => measurementGroups.reduce((t, g) => t + g.items.length, 0), []);

    const rows = useMemo(() => {
        const needle = query.trim().toLowerCase();

        return measurementGroups
            .filter(g => group === ALL || g.id === group)
            .flatMap(g => g.items.map(item => ({ ...item, category: g.title })))
            .filter(item => !needle
                || `${item.name} ${item.id} ${item.unit} ${item.type} ${item.source} ${item.definition} ${item.category}`
                    .toLowerCase()
                    .includes(needle));
    }, [query, group]);

    const exportCatalogue = () => {
        downloadCsv('SmartDC_Measurement_Specification.csv', [
            ['ID', 'Category', 'Measurement', 'Unit', 'Type', 'Instrument', 'Definition', 'Design cadence'],
            ...measurementGroups.flatMap(g => g.items.map(item => [
                item.id, g.title, item.name, item.unit, item.type, item.source, item.definition, item.target
            ]))
        ]);
        sim.notify('Catalogue exported as a CSV.');
    };

    return (
        <>
            <div className="dash-controls dash-controls--spread">
                <label className="dash-search dash-search--wide">
                    <span className="dash-sr">Search measurements</span>
                    <input type="search" placeholder="Search measurements, sensors or definitions…" value={query} onChange={event => setQuery(event.target.value)} />
                </label>

                <div className="dash-controls__end">
                    <span className="dash-axis" role="status">{rows.length} of {total} measurements</span>
                    <button type="button" className="dash-btn" onClick={exportCatalogue}>Export catalogue</button>
                </div>
            </div>

            <div className="dash-chips" role="group" aria-label="Measurement category">
                <button type="button" aria-pressed={group === ALL} onClick={() => setGroup(ALL)}>All</button>
                {measurementGroups.map(g => (
                    <button type="button" key={g.id} aria-pressed={group === g.id} onClick={() => setGroup(g.id)}>{g.title}</button>
                ))}
            </div>

            <Panel
                title={`${total} measurements across ${measurementGroups.length} disciplines`}
                sub="Measured, derived, status and configured values"
            >
                <Foot>Which measurements apply depends on the sensors fitted at a given site. Cadences are design targets.</Foot>

                {rows.length === 0 && <Note>Nothing matches that search. Try a sensor type or a shorter word.</Note>}

                {rows.length > 0 && (
                    <Table className="dash-table--wrap dash-library">
                        <thead>
                            <tr>
                                <th scope="col">Measurement</th>
                                <th scope="col">ID</th>
                                <th scope="col">Type</th>
                                <th scope="col">Unit</th>
                                <th scope="col">Instrument</th>
                                <th scope="col">Definition</th>
                                <th scope="col">Cadence</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(item => (
                                <tr key={item.id}>
                                    <th scope="row">{item.name}<span className="dash-table__note">{item.category}</span></th>
                                    <td>{item.id}</td>
                                    <td><Pill tone={item.type === 'Measured' ? 'info' : 'muted'}>{item.type}</Pill></td>
                                    <td>{item.unit}</td>
                                    <td>{item.source}</td>
                                    <td className="dash-library__def">{item.definition}</td>
                                    <td>{item.target}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}

                {rows.length > 0 && <Caption>{rows.length} matching {rows.length === 1 ? 'definition' : 'definitions'} · Three-phase AC distribution + 800 VDC UPS link · Independent A/B</Caption>}
            </Panel>

            <Panel title="Technical references" sub="Standards and primary technical references behind the measurement design">
                <Foot>The design uses configurable three-phase AC profiles and an assumed 800 V two-wire floating UPS DC link. Voltage class, grounding, ratings, sensor selection and operating limits are mapped to the actual installation. References inform the design: they do not certify this demonstration, and listing one does not imply a partnership or an endorsement.</Foot>

                <ul className="dash-refs">
                    {REFERENCES.map(ref => (
                        <li key={ref.url}>
                            <a href={ref.url} target="_blank" rel="noopener noreferrer">
                                <strong>{ref.title} <span aria-hidden="true">↗</span></strong>
                                <span>{ref.text}</span>
                            </a>
                        </li>
                    ))}
                </ul>
            </Panel>
        </>
    );
}
